import React, { useMemo, useState } from "react";
import { Calculator, TrendUp, Sparkle } from "@phosphor-icons/react";
import { calculateROI } from "../../lib/api";
import { toast } from "sonner";

const fmtUSD = (n) =>
  new Intl.NumberFormat("ru-RU", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export const ROICalculator = ({ onDemo }) => {
  const [gpuCount, setGpuCount] = useState(256);
  const [util, setUtil] = useState(38);
  const [cost, setCost] = useState(2.5);
  const [loading, setLoading] = useState(false);
  const [serverResult, setServerResult] = useState(null);

  // Local quick estimate (instant feedback)
  const local = useMemo(() => {
    const target = 85;
    const delta = Math.max(0, target - util);
    const wasted = gpuCount * 24 * 365 * (delta / 100);
    const savings = wasted * cost;
    const unlock = (target / Math.max(util, 1) - 1) * 100;
    return {
      annual_savings_usd: Math.max(0, Math.round(savings)),
      capacity_unlock_percent: Math.max(0, +unlock.toFixed(1)),
      new_utilization: target,
    };
  }, [gpuCount, util, cost]);

  const result = serverResult || local;

  const onCompute = async () => {
    setLoading(true);
    try {
      const r = await calculateROI({
        gpu_count: gpuCount,
        current_utilization: util,
        avg_gpu_cost_per_hour: cost,
      });
      setServerResult(r);
      toast.success("Расчёт обновлён", { description: "Используем серверную модель" });
    } catch (e) {
      toast.error("Не удалось пересчитать", { description: "Показан локальный расчёт" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="roi" data-testid="roi-section" className="py-24 sm:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-7">
            <div className="mono text-xs uppercase tracking-[0.22em] text-brand-cyan mb-3">
              / 06 — Калькулятор ROI
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              Сколько вы теряете
              <br />
              <span className="text-brand-cyan">каждый час?</span>
            </h2>
            <p className="mt-5 text-muted max-w-2xl leading-relaxed">
              Двигайте параметры — расчёт обновляется в реальном времени. Серверный
              расчёт учитывает амортизацию, электричество и эффект MIG-партиций.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-7 glass rounded-2xl p-8 sm:p-10">
            <div className="space-y-8">
              <Row
                label="Количество GPU"
                value={`${gpuCount}`}
                unit="карт"
                input={
                  <input
                    type="range"
                    min={16}
                    max={4096}
                    step={16}
                    value={gpuCount}
                    onChange={(e) => { setGpuCount(+e.target.value); setServerResult(null); }}
                    className="brand-range"
                    data-testid="roi-gpu-count"
                  />
                }
                hint="16 → 4096"
              />
              <Row
                label="Текущая утилизация GPU"
                value={`${util}%`}
                unit=""
                input={
                  <input
                    type="range"
                    min={10}
                    max={80}
                    step={1}
                    value={util}
                    onChange={(e) => { setUtil(+e.target.value); setServerResult(null); }}
                    className="brand-range"
                    data-testid="roi-util"
                  />
                }
                hint="10% → 80%"
              />
              <Row
                label="Средняя стоимость GPU-часа"
                value={`$${cost.toFixed(2)}`}
                unit="/ час"
                input={
                  <input
                    type="range"
                    min={0.5}
                    max={6}
                    step={0.1}
                    value={cost}
                    onChange={(e) => { setCost(+e.target.value); setServerResult(null); }}
                    className="brand-range"
                    data-testid="roi-cost"
                  />
                }
                hint="$0.50 → $6.00"
              />
            </div>

            <button
              onClick={onCompute}
              disabled={loading}
              data-testid="roi-compute-btn"
              className="mt-10 btn-primary rounded-lg px-6 py-3 font-semibold inline-flex items-center gap-2"
            >
              <Calculator size={18} weight="bold" />
              {loading ? "Считаем…" : "Точный расчёт"}
            </button>
          </div>

          {/* Result */}
          <div className="lg:col-span-5 glass-strong rounded-2xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-cyan/15 blur-[80px]" />
            <div className="relative">
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-brand-cyan mb-3 flex items-center gap-2">
                <Sparkle size={12} weight="fill" /> Annual Savings
              </div>
              <div
                className="font-heading font-black tracking-tighter mono text-5xl sm:text-6xl bg-gradient-to-r from-brand to-brand-cyan bg-clip-text text-transparent text-glow-cyan"
                data-testid="roi-result-savings"
              >
                {fmtUSD(result.annual_savings_usd)}
              </div>
              <div className="mt-2 mono text-xs uppercase tracking-widest text-muted">
                на текущем парке GPU
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="rounded-xl bg-ink-elevated border border-ink-text/5 p-4">
                  <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-muted mb-2">
                    <TrendUp size={12} weight="bold" /> Util target
                  </div>
                  <div
                    className="mono text-2xl font-bold text-brand-cyan"
                    data-testid="roi-result-util"
                  >
                    {result.new_utilization}%
                  </div>
                </div>
                <div className="rounded-xl bg-ink-elevated border border-ink-text/5 p-4">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted mb-2">
                    Capacity unlock
                  </div>
                  <div
                    className="mono text-2xl font-bold text-brand-cyan"
                    data-testid="roi-result-capacity"
                  >
                    +{result.capacity_unlock_percent}%
                  </div>
                </div>
              </div>

              <button
                onClick={onDemo}
                data-testid="roi-cta-demo"
                className="mt-8 w-full btn-secondary rounded-lg px-6 py-3 font-semibold"
              >
                Получить детальный отчёт →
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Row = ({ label, value, unit, input, hint }) => (
  <div>
    <div className="flex items-baseline justify-between mb-3">
      <div className="font-heading font-semibold text-sm">{label}</div>
      <div className="mono text-brand-cyan text-lg">
        {value} <span className="text-muted text-xs">{unit}</span>
      </div>
    </div>
    {input}
    <div className="mt-2 mono text-[10px] uppercase tracking-widest text-muted/70">{hint}</div>
  </div>
);

export default ROICalculator;
